import { useState } from "react";
import toast from "react-hot-toast";
import {
  Form,
  redirect,
  ActionFunctionArgs,
  LoaderFunctionArgs,
  useLoaderData,
} from "react-router-dom";
import IndeterminateCheckbox from "../../components/inputs/IndeterminantCheckbox";
import { Input } from "../../components/inputs/Input";
import CustomSelect from "../../components/customSelect";
import { FormLayout } from "../../layout/FormLayout";
import { createApolloClient } from "../../providers/ApolloClientFactory";
import {
  organizationStore,
  useOrganizationStore,
} from "../../store/organization";
import { PaymentProcessor } from "../../types/paymentProcessor";
import { CreatePaymentProcessor } from "./mutations";
import { PAYMENT_PROCESSOR_CATAGORIES, PaymentProcessorById } from "./queries";
import PercentageInput from "../../components/inputs/PercentageInput";
import { userStore } from "../../store/user";
import { Categories } from "../../types/item";
import PriceInput from "../../components/inputs/PriceInput";

const client = createApolloClient();
const { getState: getUserStore } = userStore;
const { getState: getOrganizationStore } = organizationStore;

export const paymentProcessorCloneLoader = async ({
  params,
}: LoaderFunctionArgs) => {
  const { id } = params;
  const organizationId = getOrganizationStore().organizationId;
  const { data } = await client.query({
    query: PaymentProcessorById,
    variables: {
      id,
    },
  });
  const { data: paymentTypeData } = await client.query({
    query: PAYMENT_PROCESSOR_CATAGORIES,
    variables: {
      where: organizationId
        ? {
            organization_id: {
              _in: [0, organizationId],
            },
            is_active: {
              _eq: true,
            },
            reference_type: {
              _eq: "payment_processors_processor",
            },
          }
        : {},
    },
  });
  return {
    paymentProcessor: data.payment_processor_config_by_pk,
    paymentType: paymentTypeData.dashboard_reference,
  };
};

export const paymentProcessorCloneAction = async ({
  request,
}: ActionFunctionArgs) => {
  const user = getUserStore().user;

  const formData = await request.formData();
  const body = Object.fromEntries(formData.entries());
  const priceString = formData.get("application_fee_per_transaction") as string;
  // Blank fee field falls back to the platform default of $0.19 — an explicit
  // "0" typed by ops is respected (parses to 0), only an EMPTY field defaults.
  const parsedPrice = parseFloat(priceString);
  const price = Number.isFinite(parsedPrice) ? parsedPrice : 0.19;
  try {
    const processingRate = (body.processing_rate || "0") as string;
    const processingRatePer = (parseFloat(processingRate) / 100).toFixed(8);
    const config =
        body.processor === "stripe"
            ? {
              connected_account_id: body.connected_account_id,
              // Deliberately NOT copied from the source row. A clone is a new
              // venue with no Terminal location of its own, and for a venue on
              // the destination flow platform_account_location_id holds a
              // location on RONIN's platform account. Copying it produced a
              // row with a platform location and no destination flag, whose
              // POS fetches a connected-scoped token and then asks Stripe to
              // connect to a platform location — which is rejected. Two rows
              // would also claim the same location. Run "Add location" on the
              // clone instead.
              platform_account_location_id: null,
              currency: body.currency || null,
              deal_structure: body.deal_structure,
              processing_rate: processingRatePer,
              application_fee_per_transaction: price
            }
            : {
              secretKey: body.secretKey,
              merchantId: body.merchantId,
              consumerKey: body.consumerKey,
              merchantAccountId: body.merchantAccountId || null,
            };
    await client.mutate({
      mutation: CreatePaymentProcessor,
      variables: {
        input: {
          payment_processor: body.payment_processor,
          processor: body.processor,
          organization_id: body.organization_id,
          config,
          is_active: body.is_active === "true",
          processing_rate: processingRatePer,
          last_updated_by: user?.id,
        },
      },
    });

    toast.success("Payment Processor Cloned");

    return redirect("/payment-processors/list", 302);
  } catch {
    toast.error("Unable To Clone Payment Processor");
    // Must return a value: react-router treats an undefined action result as
    // an invariant failure and replaces the page with the error boundary,
    // losing the user's form state. Returning null keeps them on the form.
    return null;
  }
};

export const PaymentProcessorClone = () => {
  const { paymentProcessor, paymentType } = useLoaderData() as {
    paymentProcessor: PaymentProcessor;
    paymentType: Categories[];
  };
  const [organization, setOrganization] = useState(
    paymentProcessor.organization_id
  );
  const [processor, setProcessor] = useState(paymentProcessor.processor);
  const organizations = useOrganizationStore((state) => state.organizations);
  const clonedPaymentProcessorName = `${paymentProcessor.payment_processor} - clone`;
  const [price, setPrice] = useState<number>(paymentProcessor.config.application_fee_per_transaction || 0);
  const handlePriceChange = (value: number) => {
    setPrice(value);
  };
  return (
    <Form
      method="post"
      action={`/payment-processors/${paymentProcessor.id}/clone`}
    >
      <FormLayout>
        <div className="col-span-4">
          <CustomSelect
              title="Organization"
              name="organization_id"
              defaultValue={organization}
              required
              onChange={setOrganization}
              options={organizations.map((org) => ({
                value: org.id,
                label: org.name,
              }))}
          />
        </div>
        <div className="col-span-1">
          <IndeterminateCheckbox
              label="Is Active?"
              name="is_active"
              defaultChecked={paymentProcessor.is_active}
          />
        </div>
        <div className="col-span-6">
          <CustomSelect
              title="Processor"
              required
              name="processor"
              defaultValue={paymentProcessor.processor}
              onChange={(value) => setProcessor(value as string)}
              options={[
                ...(paymentType?.map((v) => ({
                  label: v.reference_label,
                  value: v.reference_value,
                })) || []),
              ]}
          />
        </div>
        {processor === "mxmerchant" && (
            <>
              <div className="col-span-6">
                <Input
                    label="Payment Processor Name"
                    name="payment_processor"
                    required
                    defaultValue={clonedPaymentProcessorName}
                />
              </div>
              <div className="col-span-6">
                <Input
                    label="Consumer Key"
                    name="consumerKey"
                    required
                    defaultValue={paymentProcessor.config.consumerKey}
                />
              </div>
              <div className="col-span-6">
                <Input
                    label="Secret Key"
                    name="secretKey"
                    required
                    defaultValue={paymentProcessor.config.secretKey}
                />
              </div>
              <div className="col-span-6">
                <Input
                    label="Merchant ID"
                    name="merchantId"
                    required
                    defaultValue={paymentProcessor.config.merchantId}
                />
              </div>
              <div className="col-span-6">
                <PercentageInput
                    defaultValue={(
                        (paymentProcessor?.processing_rate || 0) * 100
                    ).toFixed(2)}
                    label="Processing Rate Percentage ( 7.5 = 7.5% )"
                    name="processing_rate"
                />
              </div>
            </>
        )}
        {processor === "braintree" && (
            <>
              <div className="col-span-6">
                <Input
                    label="Payment Processor Name"
                    name="payment_processor"
                    required
                    defaultValue={clonedPaymentProcessorName}
                />
              </div>
              <div className="col-span-6">
                <Input
                    label="Public Key"
                    name="consumerKey"
                    required
                    defaultValue={paymentProcessor.config.consumerKey}
                />
              </div>
              <div className="col-span-6">
                <Input
                    label="Private Key"
                    name="secretKey"
                    required
                    defaultValue={paymentProcessor.config.secretKey}
                />
              </div>
              <div className="col-span-6">
                <Input
                    label="Merchant ID"
                    name="merchantId"
                    required
                    defaultValue={paymentProcessor.config.merchantId}
                />
              </div>
              <div className="col-span-6">
                <Input
                    label="Merchant Account ID"
                    name="merchantAccountId"
                    required
                    defaultValue={paymentProcessor.config.merchantAccountId}
                />
              </div>
              <div className="col-span-6">
                <PercentageInput
                    defaultValue={(
                        (paymentProcessor?.processing_rate || 0) * 100
                    ).toFixed(2)}
                    label="Processing Rate Percentage ( 7.5 = 7.5% )"
                    name="processing_rate"
                />
              </div>
            </>
        )}
        {processor === "stripe" && (
            <>
              <div className="col-span-6">
                <Input
                    label="Payment Processor Name"
                    name="payment_processor"
                    required
                    defaultValue={clonedPaymentProcessorName}
                />
              </div>
              <div className="col-span-6">
                <Input
                    label="Connected Account ID"
                    name="connected_account_id"
                    required
                    defaultValue={paymentProcessor.config.connected_account_id}
                />
              </div>
              <div className="col-span-6">
                {/* Deliberately NOT pre-filled from the source row: a Terminal
                    Location id is scoped to one Stripe account. Copying it into
                    a clone is how rows end up pointing at locations that live
                    in some other vendor's account. */}
                <Input
                    label="Connected Account Location ID"
                    name="platform_account_location_id"
                />
              </div>
              <div className="col-span-6">
                <CustomSelect
                    title="Currency"
                    name="currency"
                    required
                    options={[
                      { value: "usd", label: "USD" },
                      { value: "cad", label: "CAD" },
                    ]}
                    defaultValue={paymentProcessor.config.currency}
                />
              </div>
              <div className="col-span-6">
                <CustomSelect
                    title="Deal Structure"
                    name="deal_structure"
                    required
                    options={[
                      { value: "percentOnTop", label: "Percent On Top" },
                      { value: "inclusiveOfCustomerCharge", label: "Inclusive of Customer Charge" },
                    ]}
                    defaultValue={paymentProcessor.config.deal_structure}
                />
              </div>
              <div className="col-span-6">
                <PercentageInput
                    defaultValue={(
                        (paymentProcessor.config.processing_rate || 0) * 100
                    ).toFixed(2)}
                    label="Processing Rate Percentage ( 7.5 = 7.5% )"
                    name="processing_rate"
                />
              </div>
              <div className="col-span-6">
                <PriceInput
                    label="Fee Per Transaction"
                    name="application_fee_per_transaction"
                    onChange={(value) => handlePriceChange(value ? value : 0)}
                    value={price}
                />
              </div>
            </>
        )}
      </FormLayout>
    </Form>
  );
};
